/**
 * Returns the file-type accept map for react-dropzone based on slot type and optional metadata.
 * If the slot has metadata-driven accepted formats, use those. Otherwise, use defaults.
 * Shared by SceneGroupedSlots and AssetSlotMapper.
 */
export const getAcceptedTypes = (type: 'image' | 'video' | 'audio', specs?: { accepted_extensions?: string[] | null; accepted_mime_types?: string[] | null } | null): Record<string, string[]> => {
  // If metadata specifies accepted types, use those
  if (specs?.accepted_mime_types && specs?.accepted_extensions) {
    const accept: Record<string, string[]> = {};
    for (let i = 0; i < specs.accepted_mime_types.length; i++) {
      accept[specs.accepted_mime_types[i]] = specs.accepted_extensions[i] ? [specs.accepted_extensions[i]] : [];
    }
    return accept;
  }

  // Otherwise, use defaults based on type
  if (type === 'image') return { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] };
  if (type === 'audio') return { 'audio/mpeg': ['.mp3'], 'audio/wav': ['.wav'] };
  return { 'video/mp4': ['.mp4'], 'video/quicktime': ['.mov'], 'video/mpeg': ['.mpeg', '.mpg'] };
};

export interface ImageSlotProp {
  type: 'image';
  uri: string;
  fit_mode?: 'cover' | 'contain' | null;
  crop_anchor?: string | null;
}

export interface VideoSlotProp {
  type: 'video';
  uri: string;
  fit_mode?: 'cover' | 'contain' | null;
  crop_anchor?: string | null;
  trim_start_seconds?: number;
  trim_duration_frames?: number;
  trim_fps?: number;
}

export interface TextSlotProp {
  type: 'text';
  value: string;
  language?: string;
}

export interface AudioSlotProp {
  type: 'audio';
  uri: string;
}

/** A slot prop is one of the typed shapes, or null for unfilled optional slots. */
export type SlotProp = ImageSlotProp | VideoSlotProp | TextSlotProp | AudioSlotProp | null;

/**
 * The full props object passed to Remotion — keyed by slot_id.
 */
export type RenderProps = Record<string, SlotProp | string>;

/** All available crop anchor positions. */
export const CROP_ANCHOR_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
] as const;

/** Maps a crop_anchor string to a CSS objectPosition value. */
export function cropAnchorToObjectPosition(anchor?: string | null): string {
  switch (anchor) {
    case 'top': return 'center top';
    case 'bottom': return 'center bottom';
    case 'left': return 'left center';
    case 'right': return 'right center';
    case 'top-left': return 'left top';
    case 'top-right': return 'right top';
    case 'bottom-left': return 'left bottom';
    case 'bottom-right': return 'right bottom';
    case 'center':
    default: return 'center center';
  }
}

/**
 * Convert legacy assetMappings array (old frontend format) to the new props structure.
 * Used as a backward-compatibility shim.
 */
export function assetMappingsToProps(
  assetMappings: Array<{
    slotId: string;
    type: string;
    storageUrl?: string;
    textValue?: string;
  }>,
  slots: Array<{
    slotId: string;
    type: string;
    specs?: { fit_mode?: string | null; crop_anchor?: string | null };
  }>,
): RenderProps {
  const props: RenderProps = {};

  for (const mapping of assetMappings) {
    const slot = slots.find((s) => s.slotId === mapping.slotId);

    if (mapping.type === 'text') {
      props[mapping.slotId] = { type: 'text', value: mapping.textValue ?? '' };
    } else if (mapping.type === 'audio' && mapping.storageUrl) {
      props[mapping.slotId] = { type: 'audio', uri: mapping.storageUrl };
    } else if (mapping.storageUrl) {
      props[mapping.slotId] = {
        type: mapping.type === 'video' ? 'video' : 'image',
        uri: mapping.storageUrl,
        fit_mode: (slot?.specs?.fit_mode as 'cover' | 'contain' | null) ?? 'cover',
        crop_anchor: slot?.specs?.crop_anchor ?? 'center',
      };
    } else {
      props[mapping.slotId] = null;
    }
  }

  return props;
}
